import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import Breadcrumb from "../../components/common/breadcrumb/Breadcrumb";
import { RootState, useAppDispatch } from "../../app/features/store/store";
import { useSelector } from "react-redux";
import {
  downloadZipCoverageDataToCsv,
  downloadZipCoverageDataToXlsx,
  getZipCoverageList,
} from "../../app/features/zipCoverage/zipCoverageSlice";
import getAxiosError from "../../app/api/axiosError";
import { toast } from "react-toastify";
import { updateUserTokenToInvalid } from "../../app/features/login/loginSlice";
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table";
import {
  ZIP_COVERAGE_COLUMN_NAME,
  ZIP_COVERAGE_LABEL_NAME,
} from "./ZipCoverageConstants";
import CommandOpsDataGrid from "../../components/common/commandOpsDataGrid/CommandOpsDataGrid";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";
import CachedIcon from "@mui/icons-material/Cached";
import DownloadIcon from "@mui/icons-material/Download";
import ZipCodeDataFilter from "./ZipCodeDataFilter";
import { downloadCsv, downloadXlsx } from "../../helpers/downloadFile";
import {
  useSubmitModal,
  useSubmitModalWithParameter,
} from "../../components/common/modalPopup/useSubmitModal";
import { PAGES } from "../../enums/PagesEnum";
import { ZipCoverageResponse } from "../../app/models/responseModels/zipCoverageResponse";
import AddZipCoverageModal from "./modals/AddZipCoverageModal";
import EditZipCoverageModal from "./modals/EditZipCoverageModal";

const breadcrumb = ["Zip Coverage"];

const ZipCoverage = () => {
  const dispatch = useAppDispatch();
  const { isLoading, zipCoverageList } = useSelector(
    (state: RootState) => state.zipCoverage
  );

  // ***** Get page Permissions *****
  const pagePermission = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings?.pagePermissions.find(
      (x) => x.pageName === PAGES.ZIP_COVERAGE
    )
  );

  const IsCreatePermission = pagePermission
    ? pagePermission.isCreatePermission
    : false;

  const IsAdmin = useSelector((state: RootState) =>
    state.login?.authResponse?.userSettings
      ? state.login?.authResponse?.userSettings?.roles.includes(
          "Administrators"
        )
      : false
  );

  // ***** States *****
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [filteredData, setFilteredData] = useState<ZipCoverageResponse[]>([]);

  // ***** Callbacks *****
  const getZipCoverageData = useCallback(async () => {
    try {
      const response = await dispatch(getZipCoverageList()).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "zipCoverageList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "zipCoverageListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    if (isFunctionRan) {
      getZipCoverageData();
    }

    return () => {
      isFunctionRan = false;
    };
  }, [getZipCoverageData]);

  useEffect(() => {
    if (zipCoverageList.length > 0) {
      if (filteredData.length === zipCoverageList.length) {
        setFilteredData(zipCoverageList);
      }
    } else {
      setFilteredData([]);
    }
  }, [zipCoverageList, filteredData]);

  // ***** UseMemos *****
  // Columns
  const columns = useMemo<MRT_ColumnDef<ZipCoverageResponse>[]>(
    () => [
      {
        accessorKey: ZIP_COVERAGE_COLUMN_NAME.PARCEL_CENTER_NAME,
        header: ZIP_COVERAGE_LABEL_NAME.PARCEL_CENTER_NAME,
        size: 80,
        filterVariant: "autocomplete",
      },
      {
        accessorKey: ZIP_COVERAGE_COLUMN_NAME.ZIP_CODE,
        header: ZIP_COVERAGE_LABEL_NAME.ZIP_CODE,
        size: 180,
        filterVariant: "autocomplete",
      },
      {
        filterVariant: "date",
        accessorKey: ZIP_COVERAGE_COLUMN_NAME.CREATED_ON,
        accessorFn: (row) => new Date(row.createdOn),
        header: ZIP_COVERAGE_LABEL_NAME.CREATED_ON,
        size: 230,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.createdOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h24",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
      {
        accessorKey: ZIP_COVERAGE_COLUMN_NAME.UPDATED_ON,
        header: ZIP_COVERAGE_LABEL_NAME.UPDATED_ON,
        size: 230,
        Cell: ({ cell, row }) => (
          <div>
            {row.original.updatedOn ? (
              <span>
                {new Date(cell.getValue<string>()).toLocaleString("en-US", {
                  hourCycle: "h24",
                })}
              </span>
            ) : (
              <span></span>
            )}
          </div>
        ),
      },
    ],
    []
  );

  // ***** Handlers *****
  const handleRefresh = () => {
    getZipCoverageData();
  };

  const handleDownloadMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCsvFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadZipCoverageDataToCsv(filteredData)
      ).unwrap();

      if (response.isSuccess) {
        downloadCsv(response.data);
      } else {
        toast.error(response.message!, {
          toastId: "zipCoverageDownloadCsv",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "zipCoverageDownloadCsvException",
      });
    }
  };

  const handleXlsxFileDownload = async (
    event: React.MouseEvent<HTMLElement>
  ) => {
    handleClose();
    event.preventDefault();
    try {
      const response = await dispatch(
        downloadZipCoverageDataToXlsx(filteredData)
      ).unwrap();

      if (response) {
        downloadXlsx(response, "ZipCoverage.xlsx");
      } else {
        toast.error("unable to download file", {
          toastId: "zipCoverageDownloadXlsx",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "zipCoverageDownloadXlsxException",
      });
    }
  };

  const [addZipCoverageModal, openAddZipCoverageModal] = useSubmitModal(
    (closeAddZipCoverageModal) => (
      <AddZipCoverageModal
        closeAddZipCoverageModal={closeAddZipCoverageModal}
        onCompleted={getZipCoverageData}
      />
    )
  );

  const [editZipCoverageModal, openEditZipCoverageModal] =
    useSubmitModalWithParameter<ZipCoverageResponse>(
      (zipCoverageRowData, closeEditZipCoverageModal) => (
        <EditZipCoverageModal
          closeEditZipCoverageModal={closeEditZipCoverageModal}
          onCompleted={getZipCoverageData}
          zipCoverageRowData={zipCoverageRowData}
        />
      )
    );

  // ***** Datagrid Custom Actions *****
  const renderTopToolbarCustomActions = (
    table: MRT_TableInstance<ZipCoverageResponse>
  ) => {
    return (
      <Box display="flex" alignContent="center">
        <CustomTooltip arrow title="Refresh">
          <IconButton onClick={handleRefresh}>
            <CachedIcon sx={{ color: "primary.main" }} />
          </IconButton>
        </CustomTooltip>
        <CustomTooltip arrow title="Download/Export">
          <IconButton type="button" onClick={handleDownloadMenuClick}>
            <DownloadIcon sx={{ color: "blue" }} />
          </IconButton>
        </CustomTooltip>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          <MenuItem onClick={(event) => handleCsvFileDownload(event)}>
            Csv
          </MenuItem>
          <MenuItem onClick={(event) => handleXlsxFileDownload(event)}>
            Xlsx
          </MenuItem>
        </Menu>
        {IsCreatePermission && IsAdmin && (
          <Button
            color="success"
            disabled={isLoading}
            onClick={() => openAddZipCoverageModal()}
            variant="contained"
            size="small"
            sx={{ ml: 1 }}
          >
            Add
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Breadcrumb pageNameList={breadcrumb} />
      <>
        <ZipCodeDataFilter
          zipCoverageData={zipCoverageList}
          setFilteredData={setFilteredData}
          filteredData={filteredData}
        />
        <Divider />
        <br />
      </>
      <CommandOpsDataGrid
        data={filteredData}
        columns={columns}
        isLoading={isLoading}
        renderTopToolbarCustomActions={renderTopToolbarCustomActions}
        emptyRowMessage={"No zip coverage data"}
        rowCount={filteredData.length.toLocaleString()}
        isDescending={true}
        defaultSortColumnName="createdOn"
        openModal={openEditZipCoverageModal}
      />
      {addZipCoverageModal}
      {editZipCoverageModal}
    </Box>
  );
};

export default ZipCoverage;
